/**
 * SPATIAL_FILTER (AcDbSpatialFilter / AcDbFilter) OBJECTS-section writer.
 *
 * EPIC-VIEWPORT-CLIP SLICE 2 — `S-OBJECTS-SPATIAL-FILTER`. Inverse of
 * `objects/spatialFilterParser.ts`; the round-trip
 * `parse → serialize → parse` is exercised by
 * `objects/spatialFilter.test.ts`. Mirrors `serializeAcDbScale`
 * (`objects/objectsAnnotativeWriter.ts`) — the writer RECONSTRUCTS the
 * SPATIAL_FILTER record from the typed `DxfSpatialFilter` list (the record
 * is pulled out of `rawObjectGroups` at parse time, exactly like AcDbScale).
 *
 * ## Emission order (byte-faithful against AutoCAD / ezdxf
 * `spatial_filter.py::export_entity` — the canonical hand-written order,
 * NOT DXFAttr declaration order):
 *
 * ```
 * 0   <objectType>                 ← original group-0 name (vendor-preserved)
 * 5   <handle>
 * 330 <ownerHandle>                ← if present
 * 100 AcDbFilter
 * 100 AcDbSpatialFilter
 * 70  <boundary point count>
 * 10/20  <clip boundary point>     ← repeats <count> times
 * 210/220/230 <extrusion>          ← if present
 * 11/21/31    <origin>             ← if present
 * 71  <clip boundary display flag> ← only when source carried a 71 (or synthetic)
 * 72  <has front clipping plane>   ← always emitted
 * 40  <front clip distance>        ← ONLY when 72 == 1
 * 73  <has back clipping plane>    ← always emitted
 * 41  <back clip distance>         ← typed ONLY when 73 == 1
 * 40 x12  <inverse_insert_matrix>
 * 40 x12  <transform_matrix>
 * ```
 *
 * The conditional front-clip distance (code 40) is emitted BEFORE the two
 * matrix blocks (also code 40) — this exact positioning is what makes the
 * parser's "last 24 code-40 reals = matrices" disambiguation correct.
 *
 * ## Present-iff-ordinal typed emission (Codex #319 P2 — findings 1–5)
 *
 * For a PARSED record the writer reconstructs the source group sequence PURELY
 * from the ordinal stream: it emits a typed group IFF that group had a source
 * ordinal (i.e. was present in source). It NEVER synthesises / defaults an
 * omitted typed group — an omitted 72/73 stays omitted, an enabled clip plane
 * whose source lacked its 40/41 distance does NOT gain an invented one, a
 * partial extrusion triplet (only 210) re-emits only 210, a disabled-but-present
 * 41 is preserved (as a RAW group at its ord). Source presence is carried in
 * `DxfSpatialFilter.presence`, each flag mirroring the parser's `addTyped` gate.
 * Because every emitted typed group then has a source ordinal, the present-
 * filtered typed list length equals `typedOrds.length` and the ordinal merge
 * below never falls back to the append path (which is what previously moved raw
 * groups out of position — finding 5).
 *
 * ## Raw-group re-emission — ABSOLUTE SOURCE-ORDINAL merge (Codex #319 P2)
 *
 * A PARSED record carries `typedOrds` (the source ordinal of EACH emitted typed
 * group, in THIS writer's canonical emit order) and `rawGroupsOrdinal` (each
 * preserved raw group with its absolute source ord). The writer builds its
 * present-filtered typed group list, ZIPS each with `typedOrds[i]`, then MERGES
 * the typed-(ord,group) entries with the raw-(ord,group) entries by ascending
 * ord. Because every group — typed OR raw — re-emits at its true source
 * position, a raw group at ANY sub-position (between a 10 and its 20, inside one
 * of the two 40×12 matrix blocks, after a leading 40, a non-promoted 41, …)
 * round-trips byte-identically by construction. This SUPERSEDES the old coarse
 * named-"slot" anchor model, which could only place a raw group AFTER a named
 * typed field and so was lossy for sub-positions.
 *
 * A SYNTHETIC record (built in code, not parsed — `typedOrds`, `presence` AND
 * `rawGroupsOrdinal` all absent) has no source order to honour: the writer
 * emits the FULL canonical typed sequence with documented defaults (extrusion
 * Z = 1, the 71, both clip flags, each enabled distance), then appends the flat
 * `rawGroups` at the end (the only sensible fallback). The writer also falls
 * back to this path defensively if the zip would misalign (present-filtered
 * typed-group count ≠ `typedOrds.length`), which can only happen for a
 * pathological parsed record (e.g. a SPATIAL_FILTER with NO boundary groups).
 */

import type { DxfGroup } from "../types";
import { pushGroup } from "./groupCodec";
import type { DxfSpatialFilter } from "./spatialFilterTypes";

const SUBCLASS_FILTER = "AcDbFilter";
const SUBCLASS_SPATIAL_FILTER = "AcDbSpatialFilter";

/**
 * Serialize a single SPATIAL_FILTER record as DXF groups. Inverse of
 * `parseSpatialFilterEntry`.
 */
export function serializeSpatialFilter(filter: DxfSpatialFilter): DxfGroup[] {
  // A PARSED record carries `presence` (+ `typedOrds`): the writer emits each
  // typed group ONLY when its source ordinal exists, so it NEVER synthesises an
  // omitted flag / distance / extrusion-axis (Codex #319 P2, findings 1–5).
  // A SYNTHETIC record (built in code) has NO presence: every canonical field is
  // emitted with its documented default (extrusion Z = 1, the 71, both flags).
  const isParsed = filter.typedOrds !== undefined;
  const presence = filter.presence;
  // `present(parsedFlag, syntheticDefault)` — for a parsed record the source
  // presence governs; for a synthetic record the canonical default governs.
  const present = (parsedFlag: boolean, syntheticDefault: boolean): boolean =>
    isParsed ? parsedFlag : syntheticDefault;

  // Build the typed groups in CANONICAL emit order (the fixed AutoCAD/ezdxf
  // sequence) AS `{ group, emit }` entries. This order MUST match the order the
  // parser stamps `typedOrds`. `emit` is the present-iff-ordinal gate; after the
  // build we filter to `emit === true`, so the resulting typed list aligns 1:1
  // with `typedOrds` (length-equal ⇒ the ordinal zip never falls back).
  const entries: { group: DxfGroup; emit: boolean }[] = [];
  const add = (code: number, value: string | number, emit: boolean): void => {
    const out: DxfGroup[] = [];
    pushGroup(out, code, value);
    entries.push({ group: out[0]!, emit });
  };

  // Group-0 record type: re-emit the ORIGINAL name verbatim for a vendor
  // record whose group-0 was NOT the canonical "SPATIAL_FILTER" but which
  // carried the AcDbSpatialFilter subclass marker (so `isSpatialFilterType`
  // accepted it). Canonicalising to "SPATIAL_FILTER" would change the object's
  // DXF class name and break files whose CLASSES / third-party data expect the
  // registered vendor type (Codex #319 P2, finding 3 — preserve-source). The
  // group-0 header is ALWAYS present (the parser always records its ord).
  add(0, filter.objectType ?? "SPATIAL_FILTER", true);
  // Handle / owner: presence is the field itself (the parser records their ords
  // iff the value is non-empty).
  if (filter.handle) add(5, filter.handle, true);
  if (filter.ownerHandle) add(330, filter.ownerHandle, true);
  // Both subclass markers: a parsed record gates on per-marker presence; a
  // synthetic record always emits both (canonical AutoCAD output).
  add(100, SUBCLASS_FILTER, present(presence?.subclassFilter === true, true));
  add(100, SUBCLASS_SPATIAL_FILTER, present(presence?.subclass === true, true));

  // Boundary polygon. A MALFORMED source block (declared 70 ≠ pair count, or an
  // orphan ordinate) is re-emitted VERBATIM (preserve-source-never-infer): the
  // parser carried the raw 70 + 10/20 groups in `malformedBoundary`, pushed
  // unchanged so the malformed record is byte-identical. A well-formed block is
  // reconstructed from the typed points with a re-derived 70 = length. Every
  // boundary group is always emitted (its ords are all in `typedOrds`).
  if (filter.malformedBoundary) {
    for (const g of filter.malformedBoundary) entries.push({ group: g, emit: true });
  } else {
    add(70, filter.boundaryPoints.length, true);
    for (const p of filter.boundaryPoints) {
      add(10, p.x, true);
      add(20, p.y, true);
    }
  }

  // Optional 3D fields — PER-AXIS presence (Codex #319 P2, finding 4): a PARTIAL
  // source triplet (e.g. only 210) re-emits ONLY the axes whose source ordinal
  // existed, never synthesising the absent ones. The defaulted VALUE (extrusion
  // Z = 1) stays in `filter.extrusion` for slice-3 but is emitted only when the
  // axis was present. A SYNTHETIC record (no presence) emits the full triplet.
  if (filter.extrusion) {
    add(210, filter.extrusion.x, present(presence?.extrusionX === true, true));
    add(220, filter.extrusion.y, present(presence?.extrusionY === true, true));
    add(230, filter.extrusion.z, present(presence?.extrusionZ === true, true));
  }
  if (filter.origin) {
    add(11, filter.origin.x, present(presence?.originX === true, true));
    add(21, filter.origin.y, present(presence?.originY === true, true));
    add(31, filter.origin.z, present(presence?.originZ === true, true));
  }

  // DXF 71 (clip boundary display flag). Emitted when the source carried a 71
  // (`clipActivePresent`) OR for a synthetic record (always emit the canonical
  // 71). A PARSED record that OMITTED its 71 must NOT gain a synthesised one
  // (Codex #319 P2, omitted-71).
  add(71, filter.clipActive ? 1 : 0, present(filter.clipActivePresent === true, true));

  // Front clipping plane. PARSED: the 72 flag is emitted IFF the source carried
  // it (`presence.frontFlag`); the 40 distance IFF the source carried a typed
  // front distance (`presence.frontDist`) — NEVER invented for an enabled plane
  // whose source omitted the 40 (finding 2), and a disabled-but-present 40 is a
  // RAW group merged by ord. SYNTHETIC: emit the 72 always; the 40 iff enabled
  // (AutoCAD rejects tag 40 when front clipping is disabled).
  add(72, filter.frontClip.enabled ? 1 : 0, present(presence?.frontFlag === true, true));
  add(
    40,
    filter.frontClip.distance,
    present(presence?.frontDist === true, filter.frontClip.enabled),
  );
  // Back clipping plane — same contract as the front plane (73 + conditional 41).
  add(73, filter.backClip.enabled ? 1 : 0, present(presence?.backFlag === true, true));
  add(
    41,
    filter.backClip.distance,
    present(presence?.backDist === true, filter.backClip.enabled),
  );

  // The two 12-real matrices (inverse first, then forward) — both code 40,
  // 4×3 column-major, emitted AFTER the conditional front-clip distance. Always
  // emitted (their ords are all in `typedOrds`).
  for (const v of filter.inverseInsertMatrix) add(40, v, true);
  for (const v of filter.transformMatrix) add(40, v, true);

  // Present-filtered typed list — the groups whose source ordinal exists, in
  // canonical order. For a parsed record this aligns 1:1 with `typedOrds`.
  const typed = entries.filter((e) => e.emit).map((e) => e.group);

  // ── Merge typed + raw by ABSOLUTE source ordinal ──────────────────────
  const typedOrds = filter.typedOrds;
  const rawOrdinal = filter.rawGroupsOrdinal;
  if (typedOrds !== undefined && typedOrds.length === typed.length) {
    // Parsed record: zip each typed group with its source ord, merge with the
    // raw groups by ascending ord, and emit. ords are unique per source group
    // (no tie-break needed) but use a stable sort anyway.
    const merged: { ord: number; group: DxfGroup }[] = [];
    for (let i = 0; i < typed.length; i++) {
      merged.push({ ord: typedOrds[i]!, group: typed[i]! });
    }
    if (rawOrdinal) {
      for (const r of rawOrdinal) merged.push({ ord: r.ord, group: r.group });
    }
    stableSortByOrd(merged);
    return merged.map((m) => m.group);
  }

  // Synthetic record (or defensive zip-misalign fallback): canonical typed
  // order, then append the flat `rawGroups` at the end (no source order to
  // honour). This is the only sensible position when ordinals are absent.
  const out = [...typed];
  if (filter.rawGroups) {
    for (const g of filter.rawGroups) out.push(g);
  }
  return out;
}

/**
 * Stable ascending sort by `ord`. Source ordinals are unique per group so the
 * stability only matters for the defensive synthetic-70 (firstOrd − 0.5) and
 * boundary sub-ordinals the parser may emit; keeping it stable guarantees a
 * deterministic order regardless of the JS engine's sort.
 */
function stableSortByOrd(items: { ord: number; group: DxfGroup }[]): void {
  items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => a.item.ord - b.item.ord || a.index - b.index)
    .forEach(({ item }, i) => {
      items[i] = item;
    });
}
